// src/pages/resources/ChapterLongQuestionsTab.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Stack, Card, CardContent, Chip, Divider,
    Grid, Paper, Snackbar, Alert, CardActions
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, VideoLibrary as VideoIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { deleteMultiMarkQuestion } from '../../services/chapterService';

interface Props {
    chapterId: number;
    data: any[];
    refreshData: () => void;
    marks: number;
    title: string;
}

export default function ChapterLongQuestionsTab({ chapterId, data, refreshData, marks, title }: Props) {
    const navigate = useNavigate();
    const [snackbar, setSnackbar] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const isPdfTab = marks === 6;

    const showSnack = (msg: string, type: 'success' | 'error') => setSnackbar({ msg, type });

    const handleOpenCreate = () => {
        const nextNum = data && data.length > 0 ? Math.max(...data.map(q => q.questionNumber || 0)) + 1 : 1;
        navigate(`/chapters/resources/${chapterId}/question/${marks}`, { state: { nextQuestionNumber: nextNum } });
    };

    const handleOpenEdit = (q: any) => {
        navigate(`/chapters/resources/${chapterId}/question/${marks}`, { state: { editQuestion: q } });
    };

    const handleDelete = async (id: number) => {
        const confirmMsg = isPdfTab ? 'Delete this PDF completely?' : 'Delete this question completely?';
        if (!window.confirm(confirmMsg)) return;
        try {
            await deleteMultiMarkQuestion(chapterId, id);
            showSnack('Deleted', 'success');
            refreshData();
        } catch (e) {
            showSnack('Delete failed', 'error');
        }
    };

    return (
        <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                <Typography variant="h6" fontWeight={700} color="#174143">{title}</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ bgcolor: '#174143' }}>
                    {isPdfTab ? 'Add PDF' : 'Add Question'}
                </Button>
            </Box>

            {data?.map((q: any) => {
                const videoRes = q.resources?.find((r: any) => r.type === 'VIDEO');
                return (
                    <Card key={q.id} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <CardContent>
                            <Stack direction="row" spacing={1} mb={2} alignItems="center">
                                <Chip label={isPdfTab ? `PDF ${q.questionNumber || '?'}` : `Q${q.questionNumber || '?'}`} color="primary" size="small" />
                                <Chip label={isPdfTab ? 'PDF' : `${q.marks} Marks`} variant="outlined" size="small" />
                            </Stack>

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 8 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
                                        {q.question}
                                    </Typography>
                                    {q.description && (
                                        <Typography variant="body2" color="text.secondary" paragraph>{q.description}</Typography>
                                    )}
                                    {q.questionImageUrl && (
                                        <Box mt={1} component="img" src={q.questionImageUrl} alt={isPdfTab ? 'PDF Cover' : 'Question'}
                                             sx={{ maxHeight: 200, maxWidth: '100%', borderRadius: 2, border: '1px solid #eee' }}
                                        />
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    {isPdfTab ? (
                                        videoRes ? (
                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <Typography variant="caption" fontWeight={700} color="error" display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={2}>
                                                    <PdfIcon fontSize="inherit" /> PDF DOCUMENT
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<PdfIcon />}
                                                    href={videoRes.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    fullWidth
                                                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                                                >
                                                    View PDF
                                                </Button>
                                            </Paper>
                                        ) : (
                                            <Box sx={{ height: '100%', minHeight: 120, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                                <PdfIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                                <Typography variant="caption" color="text.secondary">No PDF uploaded</Typography>
                                            </Box>
                                        )
                                    ) : (
                                        videoRes ? (
                                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa' }}>
                                                <Typography variant="caption" fontWeight={700} color="primary" display="flex" alignItems="center" gap={0.5} mb={1}>
                                                    <VideoIcon fontSize="inherit" /> VIDEO EXPLANATION
                                                </Typography>
                                                <Box sx={{ bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
                                                    <video controls style={{ width: '100%', height: 'auto', maxHeight: 180, display: 'block' }}>
                                                        <source src={videoRes.url} />
                                                    </video>
                                                </Box>
                                            </Paper>
                                        ) : (
                                            <Box sx={{ height: '100%', minHeight: 120, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                                <VideoIcon color="disabled" />
                                                <Typography variant="caption" color="text.secondary">No Video</Typography>
                                            </Box>
                                        )
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ bgcolor: '#fdfdfd', justifyContent: 'flex-end', px: 2 }}>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEdit(q)}>Edit</Button>
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(q.id)}>Delete</Button>
                        </CardActions>
                    </Card>
                );
            })}

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)}>
                <Alert severity={snackbar?.type}>{snackbar?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
